import { useEffect, useState, useCallback } from "react";
import api, { saveToken } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Loader2, Lock, ArrowRight } from "lucide-react";
import { BrandLogo } from "@/components/BrandLogo";

export const AuthGate = ({ children }) => {
  const [status, setStatus] = useState("checking"); // checking | authed | login
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const check = useCallback(async () => {
    try {
      await api.get("/auth/verify");
      setStatus("authed");
    } catch {
      setStatus("login");
    }
  }, []);

  useEffect(() => {
    check();
    const onUnauth = () => setStatus("login");
    window.addEventListener("clonecast:unauthorized", onUnauth);
    return () => window.removeEventListener("clonecast:unauthorized", onUnauth);
  }, [check]);

  const submit = async (e) => {
    e.preventDefault();
    if (!password) return;
    setError("");
    setBusy(true);
    try {
      const { data } = await api.post("/auth/login", { password });
      saveToken(data.token);
      setPassword("");
      setStatus("authed");
    } catch (err) {
      setError(err.response?.data?.detail || "Incorrect password");
    } finally {
      setBusy(false);
    }
  };

  if (status === "checking") {
    return (
      <div className="min-h-screen bg-[var(--tg-bg)] grid place-items-center" data-testid="auth-checking">
        <Loader2 className="h-6 w-6 text-[var(--c-sky)] animate-spin" />
      </div>
    );
  }

  if (status === "authed") return children;

  return (
    <div className="min-h-screen bg-[var(--tg-bg)] grid place-items-center px-4 relative overflow-hidden">
      <div
        className="absolute inset-0 opacity-[0.07]"
        style={{
          backgroundImage:
            "radial-gradient(circle at 20% 20%, rgb(var(--c-sky-rgb)) 0, transparent 45%), radial-gradient(circle at 80% 70%, rgb(var(--c-teal-rgb)) 0, transparent 45%)",
        }}
      />
      <form
        onSubmit={submit}
        data-testid="login-form"
        className="relative w-full max-w-sm bg-[var(--tg-surface)] border border-[var(--tg-border)] rounded-2xl p-8 shadow-[0_0_60px_rgba(0,136,255,0.08)]"
      >
        <BrandLogo className="mb-6 w-[min(280px,100%)]" />

        <div className="mb-2 flex items-center gap-2 text-[var(--tg-muted)] text-sm">
          <Lock className="h-3.5 w-3.5 text-[var(--c-sky)]" />
          Enter password to continue
        </div>
        <Input
          type="password"
          autoFocus
          data-testid="login-password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder="••••••••"
          className="bg-[var(--tg-bg)] border-[var(--tg-border)] focus:border-[var(--c-sky)] text-[var(--tg-strong)]"
        />
        {error && (
          <p className="mt-2 text-xs text-[var(--c-rose)]" data-testid="login-error" role="alert">
            {error}
          </p>
        )}
        <Button
          type="submit"
          disabled={busy || !password}
          data-testid="login-submit"
          className="w-full mt-5 bg-[var(--c-sky)] hover:bg-[var(--c-sky-hover)] text-white font-semibold"
        >
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : (
            <span className="flex items-center gap-2">Unlock Dashboard <ArrowRight className="h-4 w-4" /></span>
          )}
        </Button>
      </form>
    </div>
  );
};
